import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { OrderWidgetTab } from '@pages/Trade/Widget/WidgetTop'
import { WIDGET_TYPE } from '@pages/Trade/utils/constants'

import type { DxTradeExecutableSide, InstrumentActionHandler } from '@model/DxTrade.types'

type OrderWidgetState = {
  symbol: string
  side: DxTradeExecutableSide
  tab: OrderWidgetTab
  widgetType: 'order' | 'market'
}
type OpenDepositModalArgs = {
  accountId: string
  onCloseWidget: () => void
  onClose?: () => void
}
interface UseMarketsWidgetControllerArgs {
  accountBalance: number
  isMobile: boolean
  isLiveAccount: boolean
  selectedAccountId: string
  selectedAccountLogin?: string
  openDepositModal: (props: OpenDepositModalArgs) => void
}

interface UseMarketsWidgetControllerReturn {
  widgetState: OrderWidgetState | null
  handleInstrumentAction: InstrumentActionHandler
  handleInstrumentSelect: (symbol: string) => void
  handleSideChange: (side: DxTradeExecutableSide) => void
  handleTabChange: (tab: OrderWidgetTab) => void
  handleWidgetClose: () => void
  switchToOrderFromMarket: (side: DxTradeExecutableSide) => void
}

export const useMarketsWidgetController = (
  props: UseMarketsWidgetControllerArgs
): UseMarketsWidgetControllerReturn => {
  const {
    accountBalance,
    isMobile,
    isLiveAccount,
    selectedAccountId,
    selectedAccountLogin,
    openDepositModal,
  } = props

  const location = useLocation()

  const [searchParams, setSearchParams] = useSearchParams()
  const [widgetState, setWidgetState] = useState<OrderWidgetState | null>(null)

  const prevLoginRef = useRef<string | undefined>(selectedAccountLogin)
  const lastDepositKeyRef = useRef<string | null>(null)
  const suppressQueryModalRef = useRef(false)

  // Reset widget and URL when account login changes
  useEffect(() => {
    const currentLogin = selectedAccountLogin
    const prevLogin = prevLoginRef.current

    if (prevLogin && prevLogin !== currentLogin) {
      setWidgetState(null)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete('symbol')
          next.delete('tab')
          next.delete('side')
          return next
        },
        { replace: true }
      )
    }

    prevLoginRef.current = currentLogin
  }, [selectedAccountLogin, setSearchParams])

  const widgetStateFromQuery = useMemo((): OrderWidgetState | null => {
    const symbol = searchParams.get('symbol')
    if (!symbol) return null

    const tabParam = searchParams.get('tab')
    const sideParam = searchParams.get('side')
    const widgetTypeParam = searchParams.get('widgetType')

    let tab: OrderWidgetTab
    switch (tabParam) {
      case 'order':
        tab = OrderWidgetTab.Order
        break
      case 'trades':
        tab = OrderWidgetTab.Trades
        break
      case 'chart':
        tab = OrderWidgetTab.Chart
        break
      case 'news':
        tab = OrderWidgetTab.News
        break
      default:
        tab = OrderWidgetTab.Market
    }
    const side: DxTradeExecutableSide = sideParam === 'SELL' ? 'SELL' : 'BUY'
    const widgetType: 'order' | 'market' = widgetTypeParam === 'market' ? 'market' : 'order'

    return {
      symbol,
      tab,
      side,
      widgetType,
    }
  }, [searchParams])
  useEffect(() => {
    if (widgetStateFromQuery !== null) {
      return
    }

    lastDepositKeyRef.current = null
    suppressQueryModalRef.current = false
  }, [widgetStateFromQuery])

  const handleDepositModalDismiss = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        next.delete('symbol')
        next.delete('tab')
        next.delete('side')
        next.delete('widgetType')
        return next
      },
      { replace: true }
    )

    setWidgetState(null)
    lastDepositKeyRef.current = null
    suppressQueryModalRef.current = false
  }, [setSearchParams])

  const openDepositModalWithCloseWidget = useCallback(() => {
    if (!selectedAccountId) {
      return
    }

    openDepositModal({
      accountId: selectedAccountId,
      onCloseWidget: handleDepositModalDismiss,
      onClose: handleDepositModalDismiss,
    })
  }, [openDepositModal, selectedAccountId, handleDepositModalDismiss])

  // Hydrate from URL and optionally open deposit modal (once) when balance is zero
  useEffect(() => {
    if (!widgetStateFromQuery) return

    const isOrderTab = widgetStateFromQuery.tab === OrderWidgetTab.Order
    const isZeroBalanceLive = isLiveAccount && accountBalance <= 0
    const currentKey = `${widgetStateFromQuery.symbol}-${widgetStateFromQuery.side}-${widgetStateFromQuery.tab}`

    if (isOrderTab && isZeroBalanceLive) {
      if (suppressQueryModalRef.current && lastDepositKeyRef.current === currentKey) {
        suppressQueryModalRef.current = false
        return
      }

      if (lastDepositKeyRef.current === currentKey) {
        return
      }

      lastDepositKeyRef.current = currentKey

      if (selectedAccountId) {
        openDepositModalWithCloseWidget()
      }

      // Keep widget closed; URL already stores desired state for later.
      return
    }

    setWidgetState(widgetStateFromQuery)
  }, [
    widgetStateFromQuery,
    isLiveAccount,
    accountBalance,
    selectedAccountId,
    openDepositModal,
    openDepositModalWithCloseWidget,
    location.state?.selectedTradingAccount?.id,
    handleDepositModalDismiss,
  ])

  const updateUrlWithWidgetState = useCallback(
    (state: OrderWidgetState | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (!state) {
            next.delete('symbol')
            next.delete('tab')
            next.delete('side')
            next.delete('widgetType')
            return next
          }

          next.set('symbol', state.symbol)
          next.set('tab', state.tab)
          next.set('side', state.side)
          next.set('widgetType', state.widgetType)
          return next
        },
        { replace: true }
      )
    },
    [setSearchParams]
  )

  const handleZeroBalance = useCallback(
    (nextState: OrderWidgetState): boolean => {
      if (isLiveAccount && accountBalance <= 0 && selectedAccountId) {
        lastDepositKeyRef.current = `${nextState.symbol}-${nextState.side}-${nextState.tab}`
        suppressQueryModalRef.current = true
        openDepositModalWithCloseWidget()
        return true
      }
      return false
    },
    [isLiveAccount, accountBalance, selectedAccountId, openDepositModalWithCloseWidget]
  )

  const handleInstrumentAction = useCallback<InstrumentActionHandler>(
    (instrument, action) => {
      if (!instrument.symbol) {
        return
      }

      if (action === 'marketWidget') {
        const nextState: OrderWidgetState = {
          symbol: instrument.symbol,
          side: 'BUY',
          tab: OrderWidgetTab.Chart,
          widgetType: 'market',
        }
        setWidgetState(nextState)
        updateUrlWithWidgetState(nextState)
        return
      }

      if (action === 'info') {
        const nextState: OrderWidgetState = {
          symbol: instrument.symbol,
          side: 'BUY',
          tab: OrderWidgetTab.Market,
          widgetType: 'order',
        }
        setWidgetState(nextState)
        updateUrlWithWidgetState(nextState)
        return
      }

      if (action === 'news') {
        const nextState: OrderWidgetState = {
          symbol: instrument.symbol,
          side: 'BUY',
          tab: OrderWidgetTab.News,
          widgetType: 'order',
        }
        setWidgetState(nextState)
        updateUrlWithWidgetState(nextState)
        return
      }

      const side: DxTradeExecutableSide = action === 'buy' ? 'BUY' : 'SELL'
      const nextState: OrderWidgetState = {
        symbol: instrument.symbol,
        side,
        tab: isMobile ? OrderWidgetTab.Chart : OrderWidgetTab.Order,
        widgetType: 'order',
      }

      updateUrlWithWidgetState(nextState)
      setWidgetState(nextState)
    },
    [updateUrlWithWidgetState, isMobile]
  )

  const handleInstrumentSelect = useCallback(
    (symbol: string) => {
      if (!widgetState || isMobile) {
        return
      }

      if (widgetState.widgetType !== WIDGET_TYPE.ORDER) {
        return
      }

      const nextState: OrderWidgetState = {
        ...widgetState,
        symbol,
      }

      setWidgetState(nextState)
      updateUrlWithWidgetState(nextState)
    },
    [isMobile, updateUrlWithWidgetState, widgetState]
  )

  const handleSideChange = useCallback(
    (side: DxTradeExecutableSide) => {
      if (!widgetState) return

      const nextState: OrderWidgetState = {
        ...widgetState,
        side,
      }

      setWidgetState(nextState)
      updateUrlWithWidgetState(nextState)
    },
    [updateUrlWithWidgetState, widgetState]
  )

  const handleTabChange = useCallback(
    (tab: OrderWidgetTab) => {
      if (!widgetState) return

      const nextState: OrderWidgetState = {
        ...widgetState,
        tab,
      }

      updateUrlWithWidgetState(nextState)

      if (tab === OrderWidgetTab.Order && handleZeroBalance(nextState)) {
        return
      }

      setWidgetState(nextState)
    },
    [widgetState, updateUrlWithWidgetState, handleZeroBalance]
  )

  const handleWidgetClose = useCallback(() => {
    if (
      isMobile &&
      widgetState?.widgetType === 'order' &&
      widgetState.tab === OrderWidgetTab.Order
    ) {
      const nextState: OrderWidgetState = {
        ...widgetState,
        tab: OrderWidgetTab.Chart,
        widgetType: 'order',
      }

      setWidgetState(nextState)
      updateUrlWithWidgetState(nextState)
      return
    }

    setWidgetState(null)
    updateUrlWithWidgetState(null)
  }, [isMobile, updateUrlWithWidgetState, widgetState])

  useEffect(() => {
    if (!widgetState || isMobile) {
      return
    }

    if (widgetState.widgetType !== WIDGET_TYPE.ORDER || widgetState.tab !== OrderWidgetTab.Chart) {
      return
    }

    const nextState: OrderWidgetState = {
      ...widgetState,
      tab: OrderWidgetTab.Order,
    }

    setWidgetState(nextState)
    updateUrlWithWidgetState(nextState)
  }, [isMobile, updateUrlWithWidgetState, widgetState])

  const switchToOrderFromMarket = useCallback(
    (side: DxTradeExecutableSide) => {
      if (!widgetState) {
        return
      }

      const nextState: OrderWidgetState = {
        ...widgetState,
        side,
        tab: OrderWidgetTab.Order,
        widgetType: 'order',
      }

      updateUrlWithWidgetState(nextState)

      if (handleZeroBalance(nextState)) {
        return
      }

      setWidgetState(nextState)
    },
    [widgetState, updateUrlWithWidgetState, handleZeroBalance]
  )

  return {
    widgetState,
    handleInstrumentAction,
    handleInstrumentSelect,
    handleSideChange,
    handleTabChange,
    handleWidgetClose,
    switchToOrderFromMarket,
  }
}

export type { OrderWidgetState }
